/**
 * CheckInsPage Component
 * Main page for managing check-ins with tabs: Pending, History, Insights
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardCheck, History, BarChart3, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { PendingCheckInsTab } from './PendingCheckInsTab';
import { CheckInHistoryTab } from './CheckInHistoryTab';
import { CheckInInsightsTab } from './CheckInInsightsTab';
import { CheckInCalendarTab } from './CheckInCalendarTab';

type TabType = 'pending' | 'history' | 'calendar' | 'insights';

interface TabConfig {
  id: TabType;
  label: string;
  icon: typeof ClipboardCheck;
  managerOnly?: boolean;
}

const tabs: TabConfig[] = [
  { id: 'pending', label: 'Pending', icon: ClipboardCheck },
  { id: 'history', label: 'History', icon: History },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'insights', label: 'Insights', icon: BarChart3, managerOnly: true },
];

export const CheckInsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Get active tab from URL or default to 'pending'
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(tabFromUrl || 'pending');

  // Check if user is a manager or admin (can see team check-ins)
  const isManager = user?.role === 'manager' || user?.role === 'account_admin';
  const isAccountAdmin = user?.role === 'account_admin';

  // Handle tab change with URL state
  // Only `tab` is touched: each tab keeps its own settings in the URL under its own keys.
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'pending') next.delete('tab');
        else next.set('tab', tab);
        return next;
      }
    );
  };

  // Filter tabs based on role
  const visibleTabs = tabs.filter(tab => !tab.managerOnly || isManager);

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
        <div className="px-4 sm:px-6 lg:px-10 py-6">
          <h1
            className="text-2xl sm:text-3xl font-semibold text-white"
            style={{ fontFamily: 'Fraunces, Georgia, serif' }}
          >
            Check-ins
          </h1>
          <p className="mt-1 text-sm text-stone-300">
            Track progress and stay aligned with your OKRs
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-10 py-6">
        {/* Tab Navigation */}
        <div className="border-b border-stone-200 mb-6">
          <nav className="flex gap-6" aria-label="Tabs">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    flex items-center gap-2 py-3 px-1 border-b-2 transition-colors font-medium text-sm
                    ${isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'pending' && <PendingCheckInsTab isManager={isManager} isAccountAdmin={isAccountAdmin} />}
          {activeTab === 'history' && <CheckInHistoryTab isAccountAdmin={isAccountAdmin} />}
          {activeTab === 'calendar' && <CheckInCalendarTab />}
          {activeTab === 'insights' && isManager && <CheckInInsightsTab isAccountAdmin={isAccountAdmin} />}
        </div>
      </div>
    </div>
  );
};
